/**
 * CR456: Create/Edit adapter modal
 * CR459: Added "Assigned Sources" section in edit mode for two-way linking.
 * CR460: Added "Match Rules" editor + "Preview Matches" panel in edit mode.
 */
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import type { ImageCaptureAdapter } from "./useAdapters";
import { AssignedSourcesSection } from "./AssignedSourcesSection";
import {
  MatchRulesEditor,
  type MatchRule,
  jsonToRules,
  rulesToJson,
  hasMatchRuleErrors,
  matchRuleErrorIndexes,
} from "./MatchRulesEditor";
import { PreviewMatchesPanel } from "./PreviewMatchesPanel";
import { useAdapterSources } from "./useAdapterSources";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  adapter?: ImageCaptureAdapter | null;
  onSave: (payload: Partial<ImageCaptureAdapter>) => Promise<boolean>;
}

function safeJson(val: unknown): string {
  if (val == null) return "";
  if (typeof val === "string") return val;
  return JSON.stringify(val, null, 2);
}

function tryParse(str: string): unknown | null {
  if (!str.trim()) return null;
  try { return JSON.parse(str); } catch { return null; }
}

export function AdapterFormModal({ open, onOpenChange, adapter, onSave }: Props) {
  const isEdit = !!adapter;

  const [adapterKey, setAdapterKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [entrypoint, setEntrypoint] = useState("");
  const [version, setVersion] = useState("1.0.0");
  const [enabled, setEnabled] = useState(true);
  const [timeoutMs, setTimeoutMs] = useState("");
  const [notes, setNotes] = useState("");
  const [paramSchema, setParamSchema] = useState("");
  const [defaultParams, setDefaultParams] = useState("");
  const [requiredContext, setRequiredContext] = useState<string[]>([]);
  const [ctxInput, setCtxInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // CR460: match rules state
  const [matchRulesEnabled, setMatchRulesEnabled] = useState(true);
  const [matchRules, setMatchRules] = useState<MatchRule[]>([]);

  // CR460-P: safe null guard — hook returns no-ops when key is null
  const adapterKeyForAssign = isEdit ? (adapter?.adapter_key ?? null) : null;
  const { bulkAssign } = useAdapterSources(adapterKeyForAssign);

  useEffect(() => {
    if (open && adapter) {
      setAdapterKey(adapter.adapter_key);
      setDisplayName(adapter.display_name);
      setEntrypoint(adapter.entrypoint);
      setVersion(adapter.version ?? "1.0.0");
      setEnabled(adapter.enabled);
      setTimeoutMs(adapter.default_timeout_ms?.toString() ?? "");
      setNotes(adapter.notes ?? "");
      setParamSchema(safeJson(adapter.param_schema));
      setDefaultParams(safeJson(adapter.default_params));
      setRequiredContext(Array.isArray(adapter.required_context) ? adapter.required_context : []);
      setMatchRulesEnabled(adapter.match_rules_enabled ?? true);
      setMatchRules(jsonToRules(adapter.match_rules));
    } else if (open && !adapter) {
      setAdapterKey(""); setDisplayName(""); setEntrypoint(""); setVersion("1.0.0");
      setEnabled(true); setTimeoutMs(""); setNotes(""); setParamSchema(""); setDefaultParams("");
      setRequiredContext([]);
      setMatchRulesEnabled(true); setMatchRules([]);
    }
    setErrors({});
  }, [open, adapter]);

  const addCtxTag = () => {
    const trimmed = ctxInput.trim();
    if (trimmed && !requiredContext.includes(trimmed)) {
      setRequiredContext((prev) => [...prev, trimmed]);
    }
    setCtxInput("");
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!adapterKey.trim()) errs.adapterKey = "Adapter key is required";
    if (!/^[a-z0-9_]+$/.test(adapterKey)) errs.adapterKey = "Only lowercase letters, digits, underscores";
    if (!displayName.trim()) errs.displayName = "Display name is required";
    if (!entrypoint.trim()) errs.entrypoint = "Entrypoint is required";
    if (paramSchema && !tryParse(paramSchema)) errs.paramSchema = "Invalid JSON";
    if (defaultParams && !tryParse(defaultParams)) errs.defaultParams = "Invalid JSON";
    if (hasMatchRuleErrors(matchRules)) {
      const badIndexes = matchRuleErrorIndexes(matchRules);
      errs.matchRules = `Fix regex error${badIndexes.length > 1 ? "s" : ""} in Rule${badIndexes.length > 1 ? "s" : ""} ${badIndexes.map((i) => i + 1).join(", ")} before saving`;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload: Partial<ImageCaptureAdapter> = {
      ...(isEdit ? {} : { adapter_key: adapterKey }),
      display_name: displayName,
      entrypoint,
      version,
      enabled,
      default_timeout_ms: timeoutMs ? parseInt(timeoutMs) : null,
      notes: notes || null,
      param_schema: paramSchema ? tryParse(paramSchema) : null,
      default_params: defaultParams ? tryParse(defaultParams) : null,
      required_context: requiredContext.length > 0 ? requiredContext : null,
      source: "repo",
      // CR460
      match_rules_enabled: matchRulesEnabled,
      match_rules: matchRules.length > 0 ? (rulesToJson(matchRules) as any) : null,
    };
    const ok = await onSave(payload);
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  const handleAssignFromPreview = async (sourceId: string) => {
    await bulkAssign([sourceId], tryParse(defaultParams));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Adapter: ${adapter?.adapter_key}` : "Register New Adapter"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Adapter Key */}
          {!isEdit && (
            <div className="space-y-1">
              <Label>Adapter Key *</Label>
              <Input
                value={adapterKey}
                onChange={(e) => setAdapterKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                placeholder="e.g. metro_ca"
              />
              {errors.adapterKey && <p className="text-xs text-destructive">{errors.adapterKey}</p>}
              <p className="text-xs text-muted-foreground">Immutable after creation. lowercase, underscores only.</p>
            </div>
          )}

          {/* Display Name */}
          <div className="space-y-1">
            <Label>Display Name *</Label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Metro Canada" />
            {errors.displayName && <p className="text-xs text-destructive">{errors.displayName}</p>}
          </div>

          {/* Entrypoint */}
          <div className="space-y-1">
            <Label>Entrypoint *</Label>
            <Input
              value={entrypoint}
              onChange={(e) => setEntrypoint(e.target.value)}
              placeholder="e.g. adapters.metro_ca:run"
              className="font-mono text-sm"
            />
            {errors.entrypoint && <p className="text-xs text-destructive">{errors.entrypoint}</p>}
          </div>

          {/* Version + Timeout */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Version</Label>
              <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0.0" />
            </div>
            <div className="space-y-1">
              <Label>Default Timeout (ms)</Label>
              <Input type="number" value={timeoutMs} onChange={(e) => setTimeoutMs(e.target.value)} placeholder="30000" />
            </div>
          </div>

          {/* Enabled */}
          <div className="flex items-center gap-3">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <Label>Enabled</Label>
          </div>

          {/* Required Context */}
          <div className="space-y-2">
            <Label>Required Context Paths</Label>
            <div className="flex gap-2">
              <Input
                value={ctxInput}
                onChange={(e) => setCtxInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCtxTag())}
                placeholder="e.g. urls.source_url"
                className="font-mono text-sm"
              />
              <Button type="button" variant="outline" size="sm" onClick={addCtxTag}>Add</Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {requiredContext.map((ctx) => (
                <Badge key={ctx} variant="secondary" className="flex items-center gap-1 font-mono text-xs">
                  {ctx}
                  <button onClick={() => setRequiredContext((prev) => prev.filter((c) => c !== ctx))}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          {/* Param Schema */}
          <div className="space-y-1">
            <Label>Param Schema (JSON Schema)</Label>
            <Textarea
              value={paramSchema}
              onChange={(e) => setParamSchema(e.target.value)}
              placeholder={'{"type":"object","required":["base_url"],"properties":{"base_url":{"type":"string"}}}'}
              className="font-mono text-xs min-h-[80px]"
            />
            {errors.paramSchema && <p className="text-xs text-destructive">{errors.paramSchema}</p>}
          </div>

          {/* Default Params */}
          <div className="space-y-1">
            <Label>Default Params (JSON)</Label>
            <Textarea
              value={defaultParams}
              onChange={(e) => setDefaultParams(e.target.value)}
              placeholder='{"base_url": "https://..."}'
              className="font-mono text-xs min-h-[60px]"
            />
            {errors.defaultParams && <p className="text-xs text-destructive">{errors.defaultParams}</p>}
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes about this adapter..." className="min-h-[60px]" />
          </div>

          {/* CR460: Match Rules Editor */}
          <MatchRulesEditor
            matchRulesEnabled={matchRulesEnabled}
            onMatchRulesEnabledChange={setMatchRulesEnabled}
            rules={matchRules}
            onRulesChange={setMatchRules}
          />
          {errors.matchRules && (
            <p className="text-xs text-destructive">{errors.matchRules}</p>
          )}

          {/* CR459: Assigned Sources — edit mode only */}
          {isEdit && adapter && (
            <AssignedSourcesSection
              adapterKey={adapter.adapter_key}
              defaultParams={adapter.default_params}
            />
          )}

          {/* CR460: Preview Matches — edit mode only */}
          {isEdit && adapter && (
            <PreviewMatchesPanel
              adapterKey={adapter.adapter_key}
              onAssign={handleAssignFromPreview}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save Adapter"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
